/**
 * Kit personalization modal — HTML preview overlay + add-to-cart.
 */
(function () {
  'use strict';

  const MAX_LOGOS = 9;
  const STORAGE_KEY = 'jv-kit-personalization-group';
  const MIN_SIZE = 10;
  const MAX_SIZE = 100;

  const uuid = () =>
    globalThis.crypto?.randomUUID?.() ||
    `grp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  const getCartSectionIds = () => {
    const ids = [];
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el.dataset.sectionId) ids.push(el.dataset.sectionId);
    }
    return ids;
  };

  const dispatchCartUpdate = (sections, items, variantId) => {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: items ? { items } : {},
          sourceId: 'jv-kit-personalization',
          data: {
            source: 'product-form-component',
            itemCount: 1,
            variantId: variantId ? String(variantId) : undefined,
            items: items || undefined,
            sections: sections || {},
          },
        },
      })
    );
  };

  const getProductQuantity = (root) => {
    const blockQty = root.querySelector('.jv-kit-personalization__trigger-wrap [name="quantity"]');
    if (blockQty?.value) return parseInt(blockQty.value, 10) || 1;

    const form = document.querySelector(root.dataset.productFormSelector || 'form[action*="/cart/add"]');
    if (!form) return parseInt(root.dataset.defaultQuantity, 10) || 1;
    const qtyInput = form.querySelector('[name="quantity"]');
    return qtyInput?.value ? parseInt(qtyInput.value, 10) || 1 : parseInt(root.dataset.defaultQuantity, 10) || 1;
  };

  const getVariantId = (root) => {
    const form = document.querySelector(root.dataset.productFormSelector || 'form[action*="/cart/add"]');
    const input = form?.querySelector('[name="id"]');
    return input?.value || root.dataset.variantId || '';
  };

  const getCartKitCount = () => {
    const kitEl = document.querySelector('[data-jv-cart-kit-count]');
    if (kitEl) {
      const n = parseInt(kitEl.textContent, 10);
      if (!Number.isNaN(n)) return n;
    }
    const btn = document.querySelector('[data-jv-cart-pers-btn][data-jv-kit-count]');
    if (btn) {
      const n = parseInt(btn.dataset.jvKitCount, 10);
      if (!Number.isNaN(n)) return n;
    }
    return 0;
  };

  const PERS_LINE_PROP = window.JvKitPersonalizationUpload?.PERS_LINE_PROP || '_embalagem_personalizada';

  const isPersLine = (item) =>
    window.JvKitPersonalizationUpload?.isPersLineItem?.(item) ||
    item?.properties?.[PERS_LINE_PROP] === 'true' ||
    item?.properties?._cart_packaging_personalization === 'true';

  const findPersLine = (cartData) => (cartData?.items || []).find(isPersLine);

  const computeKitCount = (cartData) =>
    (cartData?.items || []).reduce((sum, item) => (isPersLine(item) ? sum : sum + (item.quantity || 0)), 0);

  const fetchCartJson = async () => {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    return res.json();
  };

  const dispatchCartPersonalizationUpdate = (cart) => {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart || {},
          sourceId: 'jv-cart-personalization',
          data: {
            source: 'jv-cart-personalization',
            itemCount: cart?.item_count ?? 0,
            skipCartAction: true,
            sections: {},
          },
        },
      })
    );
  };

  /**
   * Sincroniza a linha de personalização com o mínimo de kits:
   * - kits < mínimo e linha existe → remove a linha (backup fica em attributes)
   * - kits >= mínimo, sem linha e backup salvo → recria a linha com as URLs salvas
   * @param {number} [minQty]
   * @param {number} [kitCount] - contagem já sem a linha de personalização
   * @returns {Promise<boolean>} true se algo mudou no carrinho
   */
  const syncCartPersonalization = async (minQty = 50, kitCount) => {
    const uploadApi = window.JvKitPersonalizationUpload;
    if (!uploadApi) return false;

    let cartData;
    try {
      cartData = await fetchCartJson();
    } catch {
      return false;
    }

    const persLine = findPersLine(cartData);
    const backup = uploadApi.readPersBackup(cartData.attributes || {});
    const count = kitCount != null ? kitCount : computeKitCount(cartData);
    const root = document.querySelector('[data-jv-kit-personalization][data-mode="cart"]');

    if (count < minQty && persLine) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: persLine.key, quantity: 0 }),
      });
      const cart = await res.json();
      dispatchCartPersonalizationUpdate(cart);
      return true;
    }

    if (count >= minQty && !persLine && backup.saved) {
      const variantId = root?.dataset.variantId;
      if (!variantId) return false;

      const properties = uploadApi.buildPersLineProperties({
        logoUrls: backup.logoUrls,
        mockupUrl: backup.mockupUrl,
      });

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1, properties }),
      });
      const data = await res.json();
      if (data?.status) return false;
      dispatchCartPersonalizationUpdate(data);
      return true;
    }

    return false;
  };

  window.JvCartPersonalization = {
    sync: syncCartPersonalization,
    clearIfBelowMin: syncCartPersonalization,
    getKitCount: getCartKitCount,
    isPersLine,
    findPersLine,
    computeKitCount,
    PERS_LINE_PROP,
  };

  class PersonalizationState {
    constructor(maxLogos) {
      this.maxLogos = maxLogos;
      this.logos = [];
      this.selectedId = null;
      this.groupId = sessionStorage.getItem(STORAGE_KEY) || uuid();
      sessionStorage.setItem(STORAGE_KEY, this.groupId);
      this.brandIndex = 1;
    }

    resetForNewBrand() {
      this.brandIndex += 1;
      this.clearLogos();
      this.selectedId = null;
    }

    resetAll() {
      this.clearLogos();
      this.selectedId = null;
      this.groupId = uuid();
      sessionStorage.setItem(STORAGE_KEY, this.groupId);
      this.brandIndex = 1;
    }

    clearLogos() {
      for (const logo of this.logos) {
        if (logo.objectUrl && logo.file) URL.revokeObjectURL(logo.objectUrl);
      }
      this.logos = [];
    }

    canAddLogo() {
      return this.logos.length < this.maxLogos;
    }

    addLogo(file) {
      if (!this.canAddLogo()) return null;
      const id = `logo-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const logo = {
        id,
        file,
        objectUrl: URL.createObjectURL(file),
        remoteUrl: null,
        name: file.name.replace(/\.[^.]+$/, ''),
        x: 0.5,
        y: 0.42,
        sizePercent: 35,
      };
      this.logos.push(logo);
      this.selectedId = id;
      return logo;
    }

    addLogoFromUrl(url, name = 'Logo') {
      if (!this.canAddLogo() || !url) return null;
      const id = `logo-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const logo = {
        id,
        file: null,
        objectUrl: url,
        remoteUrl: url,
        name,
        x: 0.5,
        y: 0.42,
        sizePercent: 35,
      };
      this.logos.push(logo);
      this.selectedId = id;
      return logo;
    }

    removeLogo(id) {
      const logo = this.logos.find((l) => l.id === id);
      if (logo?.objectUrl && logo.file) URL.revokeObjectURL(logo.objectUrl);
      this.logos = this.logos.filter((l) => l.id !== id);
      if (this.selectedId === id) {
        this.selectedId = this.logos[0]?.id || null;
      }
    }

    getSelected() {
      return this.logos.find((l) => l.id === this.selectedId) || null;
    }

    getBrandLabel() {
      return `Marca ${this.brandIndex}`;
    }
  }

  class PreviewEditor {
    constructor(stage, logoLayer, packagingImg) {
      this.stage = stage;
      this.logoLayer = logoLayer;
      this.packagingImg = packagingImg;
      this.state = null;
      this.onSelect = null;
      this.dragging = false;
      this.resizing = false;
      this.activeLogo = null;
      this.dragOffset = { x: 0, y: 0 };
      this.packagingBlobUrl = null;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);

      stage?.addEventListener('pointerdown', this.onPointerDown);
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);

      if (packagingImg?.src) {
        this.loadPackagingBlob(packagingImg.src);
      }
    }

    setState(state, onSelect) {
      this.state = state;
      this.onSelect = onSelect;
    }

    async loadPackagingBlob(src) {
      try {
        const res = await fetch(src);
        if (!res.ok) return;
        const blob = await res.blob();
        this.packagingBlobUrl = URL.createObjectURL(blob);
      } catch {
        /* preview usa img normal; export pode falhar silenciosamente */
      }
    }

    getStagePoint(clientX, clientY) {
      const rect = this.stage.getBoundingClientRect();
      return {
        x: (clientX - rect.left) / rect.width,
        y: (clientY - rect.top) / rect.height,
      };
    }

    renderOverlays() {
      if (!this.logoLayer || !this.state) return;
      this.logoLayer.innerHTML = '';

      for (let i = 0; i < this.state.logos.length; i++) {
        const logo = this.state.logos[i];
        const isSelected = logo.id === this.state.selectedId;
        const el = document.createElement('div');
        el.className = `jv-kit-personalization__logo-item${isSelected ? ' is-selected' : ''}`;
        el.dataset.logoId = logo.id;
        el.style.left = `${logo.x * 100}%`;
        el.style.top = `${logo.y * 100}%`;
        el.style.width = `${logo.sizePercent}%`;

        el.innerHTML = `
          <div class="jv-kit-personalization__logo-frame">
            ${isSelected ? `<span class="jv-kit-personalization__logo-tag">Logo ${i + 1}</span>` : ''}
            <img src="${logo.objectUrl}" alt="">
            ${isSelected ? '<span class="jv-kit-personalization__resize-handle" data-jv-resize-handle></span>' : ''}
          </div>
        `;

        this.logoLayer.appendChild(el);
      }
    }

    onPointerDown(event) {
      const item = event.target.closest('.jv-kit-personalization__logo-item');
      if (!item || !this.state) return;

      const logo = this.state.logos.find((l) => l.id === item.dataset.logoId);
      if (!logo) return;

      const selectionChanged = this.state.selectedId !== logo.id;
      this.state.selectedId = logo.id;
      this.activeLogo = logo;

      if (selectionChanged) this.onSelect?.();

      if (event.target.closest('[data-jv-resize-handle]')) {
        this.resizing = true;
      } else {
        this.dragging = true;
        const point = this.getStagePoint(event.clientX, event.clientY);
        this.dragOffset = { x: point.x - logo.x, y: point.y - logo.y };
      }

      item.setPointerCapture?.(event.pointerId);
      event.preventDefault();
    }

    onPointerMove(event) {
      if (!this.activeLogo) return;

      const point = this.getStagePoint(event.clientX, event.clientY);

      if (this.dragging) {
        this.activeLogo.x = Math.min(1, Math.max(0, point.x - this.dragOffset.x));
        this.activeLogo.y = Math.min(1, Math.max(0, point.y - this.dragOffset.y));
        this.syncOverlayPosition(this.activeLogo);
      }

      if (this.resizing) {
        const rect = this.stage.getBoundingClientRect();
        const centerX = rect.left + this.activeLogo.x * rect.width;
        const dist = Math.abs(event.clientX - centerX);
        this.activeLogo.sizePercent = Math.min(
          MAX_SIZE,
          Math.max(MIN_SIZE, Math.round((dist * 2.2 / rect.width) * 100))
        );
        this.syncOverlaySize(this.activeLogo);
      }
    }

    onPointerUp() {
      this.dragging = false;
      this.resizing = false;
      this.activeLogo = null;
    }

    syncOverlayPosition(logo) {
      const el = this.logoLayer?.querySelector(`[data-logo-id="${logo.id}"]`);
      if (!el) return;
      el.style.left = `${logo.x * 100}%`;
      el.style.top = `${logo.y * 100}%`;
    }

    syncOverlaySize(logo) {
      const el = this.logoLayer?.querySelector(`[data-logo-id="${logo.id}"]`);
      if (!el) return;
      el.style.width = `${logo.sizePercent}%`;
    }

    async exportMockupBlob() {
      if (!this.state?.logos.length) return null;

      const rect = this.stage.getBoundingClientRect();
      const w = Math.round(rect.width);
      const h = Math.round(rect.height);
      if (w < 1 || h < 1) return null;

      const canvas = document.createElement('canvas');
      const dpr = window.devicePixelRatio || 1;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      try {
        if (this.packagingBlobUrl) {
          const pkg = await this.loadImage(this.packagingBlobUrl);
          const ratio = pkg.naturalWidth / pkg.naturalHeight;
          let dw = w;
          let dh = h;
          let dx = 0;
          let dy = 0;
          if (ratio > w / h) {
            dh = w / ratio;
            dy = (h - dh) / 2;
          } else {
            dw = h * ratio;
            dx = (w - dw) / 2;
          }
          ctx.drawImage(pkg, dx, dy, dw, dh);
        }

        for (const logo of this.state.logos) {
          const img = await this.loadImage(logo.objectUrl);
          const lw = (logo.sizePercent / 100) * w;
          const lh = (img.naturalHeight / img.naturalWidth) * lw;
          const lx = logo.x * w - lw / 2;
          const ly = logo.y * h - lh / 2;
          ctx.drawImage(img, lx, ly, lw, lh);
        }

        return await new Promise((resolve) => {
          canvas.toBlob(resolve, 'image/png', 0.92);
        });
      } catch {
        return null;
      }
    }

    loadImage(src) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });
    }
  }

  const initRoot = (root) => {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';

    const maxLogos = parseInt(root.dataset.maxLogos, 10) || MAX_LOGOS;
    const state = new PersonalizationState(maxLogos);

    const dialogComponent = root.querySelector('dialog-component');
    const stage = root.querySelector('[data-jv-kit-stage]');
    const logoLayer = root.querySelector('[data-jv-kit-logo-layer]');
    const packagingImg = root.querySelector('[data-jv-kit-packaging]');
    const fileInput = root.querySelector('[data-jv-kit-file-input]');
    const logoList = root.querySelector('[data-jv-kit-logo-list]');
    const logoListWrap = root.querySelector('[data-jv-kit-logo-list-wrap]');
    const logoCount = root.querySelector('[data-jv-kit-logo-count]');
    const qtyDisplay = root.querySelector('[data-jv-kit-qty-display]');
    const addBtn = root.querySelector('[data-jv-kit-add]');
    const emptyUpload = root.querySelector('[data-jv-kit-empty-upload]');
    const addLogoBtn = root.querySelector('[data-jv-kit-add-logo-btn]');
    const errorEl = root.querySelector('[data-jv-kit-error]');
    const uploadTriggers = root.querySelectorAll('[data-jv-kit-upload-trigger]');

    if (!stage || !logoLayer) return;

    const isCartMode = root.dataset.mode === 'cart';
    const minQuantity = parseInt(root.dataset.minQuantity, 10) || 50;
    const uploadApi = window.JvKitPersonalizationUpload;

    const editor = new PreviewEditor(stage, logoLayer, packagingImg);

    let uploader = null;
    try {
      uploader = uploadApi?.createUploader({
        provider: root.dataset.uploadProvider || 'shopify',
        cloudName: root.dataset.cloudinaryCloudName || '',
        uploadPreset: root.dataset.cloudinaryUploadPreset || '',
      });
    } catch (err) {
      console.warn('[jv-kit-personalization]', err);
    }

    const showError = (msg) => {
      if (!errorEl) return;
      errorEl.textContent = msg || '';
      errorEl.hidden = !msg;
    };

    const parseJsonSafe = (str, fallback) => {
      try {
        return JSON.parse(str);
      } catch {
        return fallback;
      }
    };

    const parseCartAttrs = () => {
      const script = root.querySelector('[data-jv-kit-cart-attrs]');
      return parseJsonSafe(script?.textContent, {});
    };

    const getSavedBackup = () => uploadApi?.readPersBackup(parseCartAttrs(), maxLogos) || { saved: false, logoUrls: [] };

    const loadSavedLogosIntoState = () => {
      state.clearLogos();
      getSavedBackup().logoUrls.forEach((url, i) => {
        state.addLogoFromUrl(url, `Logo ${i + 1}`);
      });
      updateLogoUI();
    };

    const openModal = () => {
      if (isCartMode) {
        if (getCartKitCount() < minQuantity) {
          showError(root.dataset.errorMinQty || 'Personalização disponível a partir de 50 unidades.');
          return;
        }
        if (root.dataset.hasPersonalization === 'true' && !state.logos.length) {
          loadSavedLogosIntoState();
        }
      }
      if (qtyDisplay) qtyDisplay.textContent = String(getProductQuantity(root));
      dialogComponent?.showDialog?.();
    };

    const closeModal = async () => {
      const dialog = dialogComponent?.refs?.dialog;
      if (!dialog?.open) return;

      await dialogComponent.closeDialog?.();

      if (dialog.open) {
        dialog.close();
      }
    };

    const updateLogoUI = ({ previewOnly = false } = {}) => {
      const count = state.logos.length;

      if (logoCount) logoCount.textContent = `${count} de ${maxLogos}`;
      if (emptyUpload) emptyUpload.hidden = count > 0;
      if (addLogoBtn) {
        addLogoBtn.hidden = count === 0 || count >= maxLogos;
        addLogoBtn.style.display = count > 0 && count < maxLogos ? '' : 'none';
      }

      if (previewOnly && logoList) {
        logoList.querySelectorAll('.jv-kit-personalization__logo-card').forEach((card, idx) => {
          card.classList.toggle('is-active', state.logos[idx]?.id === state.selectedId);
        });
      } else if (logoList) {
        logoList.innerHTML = '';
        const sizeLabel = root.dataset.labelSize || 'TAMANHO';
        const logoNLabel = root.dataset.labelLogoN || 'Logo';

        for (let i = 0; i < state.logos.length; i++) {
          const logo = state.logos[i];
          const isActive = logo.id === state.selectedId;
          const card = document.createElement('div');
          card.className = `jv-kit-personalization__logo-card${isActive ? ' is-active' : ''}`;
          card.dataset.logoId = logo.id;
          card.innerHTML = `
            <div class="jv-kit-personalization__logo-card-head">
              <img class="jv-kit-personalization__logo-card-thumb" src="${logo.objectUrl}" alt="">
              <div class="jv-kit-personalization__logo-card-info">
                <p class="jv-kit-personalization__logo-card-name">${logo.name}</p>
                <p class="jv-kit-personalization__logo-card-label">${logoNLabel} ${i + 1}</p>
              </div>
              <button type="button" class="jv-kit-personalization__logo-delete" data-jv-delete-logo aria-label="Remover logo">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 3H15M4 7H20M18 7L17 20H7L6 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
              </button>
            </div>
            <div class="jv-kit-personalization__size-row">
              <span class="jv-kit-personalization__size-label">${sizeLabel}</span>
              <input type="range" class="jv-kit-personalization__size-slider" min="${MIN_SIZE}" max="${MAX_SIZE}" value="${logo.sizePercent}" data-jv-size-slider>
              <span class="jv-kit-personalization__size-value" data-jv-size-value>${logo.sizePercent}%</span>
            </div>
          `;

          card.addEventListener('click', (e) => {
            if (e.target.closest('[data-jv-delete-logo]') || e.target.closest('[data-jv-size-slider]')) return;
            state.selectedId = logo.id;
            updateLogoUI();
          });

          card.querySelector('[data-jv-delete-logo]')?.addEventListener('click', (e) => {
            e.stopPropagation();
            state.removeLogo(logo.id);
            updateLogoUI();
          });

          const slider = card.querySelector('[data-jv-size-slider]');
          const valueEl = card.querySelector('[data-jv-size-value]');
          slider?.addEventListener('input', () => {
            logo.sizePercent = parseInt(slider.value, 10);
            if (valueEl) valueEl.textContent = `${logo.sizePercent}%`;
            editor.syncOverlaySize(logo);
          });

          logoList.appendChild(card);
        }
      }

      if (logoListWrap) {
        logoListWrap.classList.toggle('is-scrollable', count > 2);
      }

      editor.setState(state, () => updateLogoUI({ previewOnly: true }));
      editor.renderOverlays();
    };

    const debugVisible = root.dataset.debugVisibleProperties === 'true';
    const propKey = (name) => (debugVisible ? name : `_${name}`);

    const buildProperties = (logoUrls, mockupUrl, includeLogoUrls = true) => {
      const count = state.logos.length;
      const props = {
        Personalização: `${count} ${count === 1 ? 'logo' : 'logos'} · ${state.getBrandLabel()}`,
        [propKey('personalization_group')]: state.groupId,
        [propKey('logo_count')]: String(count),
        [propKey('brand_label')]: state.getBrandLabel(),
      };

      if (includeLogoUrls) {
        for (let i = 0; i < count; i++) props[propKey(`logo_${i + 1}`)] = logoUrls[i] || '';
        if (mockupUrl) props[propKey('mockup_preview')] = mockupUrl;
      }

      return props;
    };

    const buildCartAttributes = (logoUrls, mockupUrl) =>
      uploadApi.buildPersBackupAttributes({ logoUrls, mockupUrl }, maxLogos);

    const saveCartPersonalization = async () => {
      if (!state.logos.length) {
        showError(root.dataset.errorNoLogo || 'Adicione pelo menos uma logo.');
        return;
      }

      if (getCartKitCount() < minQuantity) {
        showError(root.dataset.errorMinQty || 'Personalização disponível a partir de 50 unidades.');
        return;
      }

      const variantId = root.dataset.variantId;
      if (!variantId) {
        showError(root.dataset.errorNoProduct || 'Selecione o produto de personalização no editor do tema.');
        return;
      }

      if (!uploader) {
        showError('Upload indisponível. Verifique as configurações do bloco.');
        return;
      }

      showError('');
      addBtn.disabled = true;

      const prevAddLabel = addBtn.textContent;
      addBtn.textContent = root.dataset.labelSaving || 'Salvando...';

      try {
        const backup = getSavedBackup();

        // Remove linha atual para recriar com o conjunto completo
        const cartData = await fetchCartJson();
        const persLine = findPersLine(cartData);
        if (persLine) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: persLine.key, quantity: 0 }),
          });
        }

        const mockupBlob = await editor.exportMockupBlob();
        const properties = uploadApi.buildPersLineProperties({});
        const files = {};
        let logoIndex = 0;

        if (uploader.provider === 'cloudinary') {
          for (const logo of state.logos) {
            logoIndex += 1;
            if (logo.remoteUrl && !logo.file) {
              properties[`_logo_${logoIndex}`] = logo.remoteUrl;
            } else {
              properties[`_logo_${logoIndex}`] = await uploader.uploadLogo(logo.file);
            }
          }
          if (mockupBlob) {
            properties._mockup_preview = await uploader.uploadMockup(mockupBlob);
          } else if (backup.mockupUrl) {
            properties._mockup_preview = backup.mockupUrl;
          }
        } else {
          for (const logo of state.logos) {
            logoIndex += 1;
            if (logo.remoteUrl && !logo.file) {
              properties[`_logo_${logoIndex}`] = logo.remoteUrl;
            } else {
              files[`_logo_${logoIndex}`] = logo.file;
            }
          }
          if (mockupBlob) {
            files._mockup_preview = new File([mockupBlob], 'mockup.png', { type: 'image/png' });
          } else if (backup.mockupUrl) {
            properties._mockup_preview = backup.mockupUrl;
          }
        }

        const data = await uploader.addToCart({ variantId, quantity: 1, properties, files });
        if (data?.status) throw new Error(data.description || data.message || 'Erro ao salvar');

        // Lê as URLs finais (CDN) da linha criada e faz backup nos attributes
        const updatedCart = await fetchCartJson();
        const newLine = findPersLine(updatedCart);
        const finalUrls = [];
        for (let i = 1; i <= maxLogos; i++) {
          const url = newLine?.properties?.[`_logo_${i}`];
          if (url) finalUrls.push(url);
        }
        const finalMockup = newLine?.properties?._mockup_preview || '';
        const attrs = buildCartAttributes(finalUrls, finalMockup);
        const cartAfterAttrs = await uploadApi.updateCartAttributes(attrs);

        const script = root.querySelector('[data-jv-kit-cart-attrs]');
        if (script) script.textContent = JSON.stringify(cartAfterAttrs?.attributes || attrs);
        root.dataset.hasPersonalization = 'true';

        addBtn.textContent = root.dataset.labelSaved || 'Salvo!';
        dispatchCartPersonalizationUpdate(cartAfterAttrs);

        await closeModal();
        state.resetAll();
        updateLogoUI();
        addBtn.textContent = prevAddLabel;
        if (typeof window.jvRefreshCartSummary === 'function') {
          await window.jvRefreshCartSummary();
        }
      } catch (err) {
        showError(err.message || 'Erro ao salvar personalização.');
        addBtn.textContent = prevAddLabel;
      } finally {
        addBtn.disabled = false;
      }
    };

    const addToCart = async () => {
      if (isCartMode) {
        await saveCartPersonalization();
        return;
      }

      if (!state.logos.length) {
        showError(root.dataset.errorNoLogo || 'Adicione pelo menos uma logo.');
        return;
      }

      const variantId = getVariantId(root);
      if (!variantId) {
        showError('Variante do produto não encontrada.');
        return;
      }

      const quantity = getProductQuantity(root);
      showError('');
      addBtn.disabled = true;

      const prevAddLabel = addBtn.textContent;
      addBtn.textContent = root.dataset.labelAdding || 'Adicionando...';

      try {
        const sectionIds = getCartSectionIds();
        const mockupBlob = await editor.exportMockupBlob();
        let data;

        if (uploader.provider === 'cloudinary') {
          const logoUrls = [];
          for (const logo of state.logos) {
            logoUrls.push(await uploader.uploadLogo(logo.file));
          }

          let mockupUrl = '';
          if (mockupBlob) {
            mockupUrl = await uploader.uploadMockup(mockupBlob);
          }

          const properties = buildProperties(logoUrls, mockupUrl, true);
          data = await uploader.addToCart({ variantId, quantity, properties, sectionIds });

          if (data.status) throw new Error(data.description || data.message || 'Erro ao adicionar');
        } else {
          const files = {};
          for (let i = 0; i < state.logos.length; i++) {
            files[propKey(`logo_${i + 1}`)] = state.logos[i].file;
          }
          if (mockupBlob) {
            files[propKey('mockup_preview')] = new File([mockupBlob], 'mockup.png', { type: 'image/png' });
          }

          const properties = buildProperties([], '', false);
          data = await uploader.addToCart({ variantId, quantity, properties, files, sectionIds });

          if (data.status) throw new Error(data.description || data.message || 'Erro ao adicionar');
        }

        addBtn.textContent = root.dataset.labelAdded || 'Adicionado!';
        await closeModal();
        state.resetAll();
        updateLogoUI();
        addBtn.textContent = prevAddLabel;
        dispatchCartUpdate(data.sections, data.items, variantId);
      } catch (err) {
        showError(err.message || 'Erro ao adicionar ao carrinho.');
        addBtn.textContent = prevAddLabel;
      } finally {
        addBtn.disabled = false;
      }
    };

    const handleFile = (file) => {
      if (!file) return;

      if (!/png|jpe?g/i.test(file.type)) {
        showError(root.dataset.errorFileType || 'Use PNG ou JPG.');
        return;
      }

      if (!state.canAddLogo()) {
        showError(root.dataset.errorMaxLogos || 'Limite de logos atingido.');
        return;
      }

      state.addLogo(file);
      showError('');
      updateLogoUI();
    };

    root.querySelectorAll('[data-jv-kit-open]').forEach((btn) => {
      btn.addEventListener('click', openModal);
    });
    root.addEventListener('jv-kit-open-request', openModal);

    uploadTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => fileInput?.click());
    });

    fileInput?.addEventListener('change', () => {
      handleFile(fileInput.files?.[0]);
      fileInput.value = '';
    });

    const blockQtyInput = root.querySelector('.jv-kit-personalization__trigger-wrap [name="quantity"]');
    const syncQuantityToForm = () => {
      const formQty = document.querySelector('form[action*="/cart/add"] [name="quantity"]');
      if (blockQtyInput && formQty && blockQtyInput !== formQty) {
        formQty.value = blockQtyInput.value;
        formQty.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };
    blockQtyInput?.addEventListener('change', syncQuantityToForm);
    blockQtyInput?.addEventListener('input', syncQuantityToForm);

    addBtn?.addEventListener('click', () => addToCart());

    updateLogoUI();
  };

  const init = () => {
    for (const root of document.querySelectorAll('[data-jv-kit-personalization]')) {
      initRoot(root);
    }

    document.querySelectorAll('[data-jv-open-personalization], a[href="#jv-kit-personalization-open"]').forEach((trigger) => {
      if (trigger.dataset.jvOpenBound) return;
      trigger.dataset.jvOpenBound = 'true';
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        if (trigger.disabled) return;
        const root =
          document.querySelector('[data-jv-kit-personalization][data-mode="cart"]') ||
          document.querySelector('[data-jv-kit-personalization]');
        const openBtn = root?.querySelector('[data-jv-kit-open]');
        if (openBtn) openBtn.click();
        else root?.dispatchEvent(new CustomEvent('jv-kit-open-request'));
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:load', init);
    document.addEventListener('shopify:block:select', init);
  }

  // Sincroniza a linha de personalização ao carregar (remove/recria conforme mínimo)
  document.addEventListener('DOMContentLoaded', () => {
    const root = document.querySelector('[data-jv-kit-personalization][data-mode="cart"]');
    if (!root) return;
    const minQty = parseInt(root.dataset.minQuantity, 10) || 50;
    syncCartPersonalization(minQty).then((changed) => {
      if (changed && typeof window.jvRefreshCartSummary === 'function') {
        window.jvRefreshCartSummary();
      }
    });
  });
})();
